import { cellKey } from "./schema.js";
/**
 * Board-px cap a column widens to in order to contain an element (docs/02
 * F-26/F-36). Beyond it the element is confined to the column width. Also the
 * width a migrated legacy image is fit within.
 */
export const CONTAINED_IMAGE_MAX_W = 640;
/** Transaction origin stamped on every write the image-cell migration makes. */
export const MIGRATE_IMG_CELL_ORIGIN = "migrate:img-cell";
/**
 * Displayed size for a contained image derived from its natural pixel size:
 * capped to `CONTAINED_IMAGE_MAX_W` with aspect ratio preserved. Non-positive
 * inputs pass through clamped to 0 (defensive — a real asset always has
 * positive dimensions).
 */
export function fitContainedImage(naturalW, naturalH) {
    if (naturalW <= 0 || naturalH <= 0) {
        return { w: Math.max(naturalW, 0), h: Math.max(naturalH, 0) };
    }
    if (naturalW <= CONTAINED_IMAGE_MAX_W)
        return { w: naturalW, h: naturalH };
    const scale = CONTAINED_IMAGE_MAX_W / naturalW;
    return { w: CONTAINED_IMAGE_MAX_W, h: Math.round(naturalH * scale) };
}
/**
 * Element content extent of one cell: `{w,h}` = max(dx+w), max(dy+h) over the
 * cell's contained elements (`{0,0}` when it has none). Pure, cheap, and
 * side-effect-free — consumed by BOTH the grid layout (to grow the cell, F-26)
 * and element render (to place each element relative to the resolved cell
 * rect). NOT capped: this is the raw content extent; the column-width cap lives
 * in `colElementWidth`.
 */
export function cellElementExtent(board, sheetId, rowId, colId) {
    let w = 0;
    let h = 0;
    for (const el of board.elementsForCell(sheetId, rowId, colId)) {
        if (el.anchor.kind !== "cell")
            continue; // defensive — already filtered
        const right = el.anchor.dx + el.w;
        const bottom = el.anchor.dy + el.h;
        if (right > w)
            w = right;
        if (bottom > h)
            h = bottom;
    }
    return { w, h };
}
/**
 * Pure decision core of the EXPAND-ONLY RATCHET (docs/04 §5, F-26/F-36),
 * shared by `BoardDoc.ratchetCellToElements` and any bulk pass. Given a cell's
 * element extent and the CURRENT stored row height / column width, it returns
 * only the dimensions that must GROW:
 *  - `rowHeight` iff `extent.h > currentRowHeight` (rows grow uncapped);
 *  - `colWidth = min(extent.w, CONTAINED_IMAGE_MAX_W)` iff that capped target is
 *    `> currentColWidth` (a column never widens past the 640 cap, and never
 *    below a column already wider than the cap — e.g. a manual resize).
 *
 * It NEVER proposes a smaller value (extent ≤ current → the key is omitted), so
 * the ratchet behaves like a persisted floor: text auto-fit or an element
 * moving to a smaller offset can never shrink a cell it once grew.
 */
export function planCellRatchet(extent, currentRowHeight, currentColWidth) {
    const plan = {};
    if (extent.h > currentRowHeight)
        plan.rowHeight = extent.h;
    const cappedW = Math.min(extent.w, CONTAINED_IMAGE_MAX_W);
    if (cappedW > currentColWidth)
        plan.colWidth = cappedW;
    return plan;
}
/**
 * Pure decision core of the DROP-TIME FIT (F-32/F-36, 2026-07-17), shared by
 * `BoardDoc.fitCellToElements`. Given the ROW/COLUMN-wide element extents and
 * the current stored sizes, it returns the dimensions that must CHANGE to
 * reach the fit target:
 *  - `rowHeight` target = `max(extent.h, minRowHeight)` (rows fit uncapped);
 *  - `colWidth` target = `max(min(extent.w, CONTAINED_IMAGE_MAX_W), minColWidth)`
 *    (same 640 cap as the ratchet — past it the element confines to the column).
 *
 * Unlike `planCellRatchet` it proposes SMALLER values too: dropping an element
 * out of a cell (or to a smaller offset/size) shrinks the cell back, floored at
 * the defaults the caller passes (`minRowHeight`/`minColWidth`) so an emptied
 * cell returns to its normal size. Shrinking the STORED size can never clip
 * cell text — the stored size is only a floor under the grid's derived text
 * auto-fit (docs/04 §5), which re-grows the render past it.
 */
export function planCellFit(extent, currentRowHeight, currentColWidth, minRowHeight, minColWidth) {
    const plan = {};
    const targetH = Math.max(extent.h, minRowHeight);
    if (targetH !== currentRowHeight)
        plan.rowHeight = targetH;
    const targetW = Math.max(Math.min(extent.w, CONTAINED_IMAGE_MAX_W), minColWidth);
    if (targetW !== currentColWidth)
        plan.colWidth = targetW;
    return plan;
}
/**
 * Element-driven MINIMUM height a row needs to contain its elements (F-26):
 * max(dy+h) over every element anchored to any cell in `rowId`, or 0. Rows
 * grow freely (uncapped). `sheetId` is contextual — see
 * `BoardDoc.elementsForCell`.
 */
export function rowElementHeight(board, sheetId, rowId) {
    void sheetId;
    let h = 0;
    for (const el of board.listElements()) {
        const a = el.anchor;
        if (a.kind === "cell" && a.rowId === rowId) {
            const bottom = a.dy + el.h;
            if (bottom > h)
                h = bottom;
        }
    }
    return h;
}
/**
 * Element-driven MINIMUM width a column needs to contain its elements (F-26),
 * capped at `CONTAINED_IMAGE_MAX_W`: max(dx+w) over every element anchored to
 * any cell in `colId`, or 0. `sheetId` is contextual. Delegates to
 * `containedGridExtents` so the cap and the aggregation rule live in exactly
 * ONE place (the client grid render path uses the same function — no second,
 * hand-synced copy).
 */
export function colElementWidth(board, sheetId, colId) {
    void sheetId;
    return containedGridExtents(board.listElements()).colWidthFloor.get(colId) ?? 0;
}
/**
 * Bulk contained-extent maps for one grid layout pass (F-26 growth), computed
 * in a single pass over the element list (cheap — elements are few) rather
 * than the O(cells × elements) of calling the per-cell/per-column helpers for
 * every rendered cell. This is the SINGLE source of truth for the "grow a cell
 * to contain its cell-anchored elements" rule (docs/02 F-26/F-36, docs/04 §2):
 * a column's width floor is capped at `CONTAINED_IMAGE_MAX_W` (past the cap the
 * element confines to the column width — see the client render's
 * `containedElementRect`); a cell's height floor is uncapped (rows grow
 * freely). The client grid render path and `colElementWidth` both consume this
 * — the cap is never re-implemented, so the two can never silently diverge.
 */
export function containedGridExtents(elements) {
    const cellHeight = new Map();
    const colMax = new Map();
    for (const el of elements) {
        if (el.anchor.kind !== "cell")
            continue;
        const { rowId, colId, dx, dy } = el.anchor;
        const key = cellKey(rowId, colId);
        const bottom = dy + el.h;
        if (bottom > (cellHeight.get(key) ?? 0))
            cellHeight.set(key, bottom);
        const right = dx + el.w;
        if (right > (colMax.get(colId) ?? 0))
            colMax.set(colId, right);
    }
    const colWidthFloor = new Map();
    for (const [colId, w] of colMax)
        colWidthFloor.set(colId, Math.min(w, CONTAINED_IMAGE_MAX_W));
    return { cellHeight, colWidthFloor };
}
/**
 * Legacy migration (docs/04 §2, 2026-07-14): convert every inline image cell
 * value on `sheetId` into a contained cell-anchored image element, then vacate
 * the cell's `v` key. Runs server-side once in Hocuspocus onLoadDocument
 * (single-writer-safe). ONE transaction, origin `MIGRATE_IMG_CELL_ORIGIN`
 * (kept out of every user's undo — it is a system consequence, nobody's edit).
 *
 * `genId(rowId, colId)` supplies each element id; provide a DETERMINISTIC
 * per-cell id (e.g. `(r, c) => 'mig-' + r + '-' + c`). Determinism makes the
 * bounded deploy-window dual-instance case (docs/06 §2.1) converge: both
 * instances create the same element id, Yjs map-key LWW keeps one copy, no
 * duplicate.
 *
 * Idempotent two ways: (1) only cells whose `v` is STILL an image are
 * processed, and migration clears `v`, so a re-run finds nothing to convert;
 * (2) a cell whose element id already exists is skipped. Returns the element
 * ids created on THIS run.
 */
export function migrateImageCellsToElements(board, sheetId, genId) {
    const created = [];
    board.transact(() => {
        for (const { rowId, colId, image } of board.listImageCells(sheetId)) {
            const elementId = genId(rowId, colId);
            if (!board.hasElement(elementId)) {
                const { w, h } = fitContainedImage(image.w, image.h);
                board.createImageElement(elementId, 0, // x/y are an absolute-position cache; the contained render derives
                0, // the real position from the resolved cell rect + (dx, dy).
                w, h, { assetId: image.assetId, w: image.w, h: image.h }, MIGRATE_IMG_CELL_ORIGIN, { kind: "cell", rowId, colId, dx: 2, dy: 2 });
                created.push(elementId);
            }
            // Vacate the cell's `v` (deletes the key) so the image no longer renders
            // inline and a re-run has nothing to convert.
            board.setCellValue(sheetId, rowId, colId, undefined, MIGRATE_IMG_CELL_ORIGIN);
        }
    }, MIGRATE_IMG_CELL_ORIGIN);
    return created;
}
//# sourceMappingURL=element-cell.js.map